// Utility demand rollups — P2-E (docs/implementation/PROCESS_UTILITIES.md
// Phases B + D, first slice). Pure functions over ProcessEquipment +
// EquipmentConnections rows (db/24): the canonical utility-demand source.
//
//   Equipment connection demand -> Room demand -> System (utility-type) demand
//   -> Diversity -> Effective design demand -> Plant requirement
//
// DIVERSITY — the ON/OFF model db/24's header has described since the table
// was created (no other rule exists in code or docs, so none is invented):
//   connected demand   = every utility connection's per-unit flow x INSTALLED
//                        units (Qty) — everything piped to the system
//   diversified demand = only connections with DiversityOn = 1 ("ON" in the
//                        simultaneous design case), per-unit flow x ACTIVE
//                        units (Qty - StandbyUnits; a standby unit is
//                        installed and piped but not operating)
//   design demand      = diversified demand. No statistical factor, no safety
//                        factor — a plant selection is a separate, later
//                        decision (Process Utilities Phase E records).
// Nothing is hidden in SQL; every number carries the connections that made it.
//
// UNITS — a system's governing units are its utility type's defaults
// (utilityTypes.js). Compatible units are converted with the documented
// factors below; an INCOMPATIBLE row (e.g. a CFM flow on a GPM system) is
// never silently converted — it is excluded from the totals and reported.
//
// LEGACY GENERIC DROPS — the cda_drops / n2_drops / vac_drops / pw_drops room
// criteria are reported alongside (count per room) so their relationship to
// canonical connections is explicit, but they carry NO demand and are NOT in
// any total: no Basis-of-Design per-drop demand exists yet (Phase C), and a
// drop that also has an equipment connection must never count twice.

import { UTILITY_BY_KEY, utilityLabel, isElectricalType } from "../config/utilityTypes";
import { criterionKey } from "../config/takeoffCriteria";
import { resolveLegacyCount } from "../config/roomElements";

const num = (v) => { if (v === null || v === undefined || v === "") return null; const n = Number(v); return Number.isFinite(n) ? n : null; };
const r = (v, d = 2) => v == null ? null : Math.round(v * 10 ** d) / 10 ** d;

// ---- unit conversion (documented factors; null = incompatible) ---------------------------------

// Each family: unit -> multiplier to the family's base unit.
export const FLOW_FAMILIES = {
  gas_volume:    { scfm: 1, slpm: 1 / 28.316847 },        // 1 SCFM = 28.316847 SLPM
  liquid_volume: { gpm: 1, lpm: 1 / 3.785412 },           // 1 GPM  = 3.785412 LPM
  mass:          { kg_hr: 1, lbs_hr: 1 / 2.204623 },      // 1 kg/hr = 2.204623 lbs/hr
  air_volume:    { cfm: 1 },
  heat:          { mbh: 1 },
  gas_hourly:    { cfh: 1 },
};
export const PRESSURE_TO_PSI = { psig: 1, bar: 14.503774, in_wc: 1 / 27.707627, in_hg: 1 / 2.036021 };
export const CONSUMPTION_FAMILIES = { volume_day: { gal_day: 1, l_day: 1 / 3.785412 }, mass_day: { kg_day: 1 } };

function familyOf(table, unit) {
  for (const [fam, units] of Object.entries(table)) if (unit in units) return fam;
  return null;
}
// value in `from` -> value in `to`; null when unknown or incompatible.
export function convertFlow(value, from, to) {
  const v = num(value); if (v === null) return null;
  if (!from || !to) return null;
  if (from === to) return v;
  const fam = familyOf(FLOW_FAMILIES, from);
  if (!fam || familyOf(FLOW_FAMILIES, to) !== fam) return null;
  const t = FLOW_FAMILIES[fam];
  return v * t[from] / t[to];
}
export function convertPressure(value, from, to) {
  const v = num(value); if (v === null) return null;
  if (!from || !to) return null;
  if (from === to) return v;
  if (!(from in PRESSURE_TO_PSI) || !(to in PRESSURE_TO_PSI)) return null;
  return v * PRESSURE_TO_PSI[from] / PRESSURE_TO_PSI[to];
}
export function convertConsumption(value, from, to) {
  const v = num(value); if (v === null) return null;
  if (!from || !to) return null;
  if (from === to) return v;
  const fam = familyOf(CONSUMPTION_FAMILIES, from);
  if (!fam || familyOf(CONSUMPTION_FAMILIES, to) !== fam) return null;
  const t = CONSUMPTION_FAMILIES[fam];
  return v * t[from] / t[to];
}

// ---- Phase B: normalized connection interface -------------------------------------------------

const unitsOf = (eq) => {
  const qty = Math.max(0, num(eq?.Qty) ?? 1), standby = Math.max(0, num(eq?.StandbyUnits) ?? 0);
  return { qty, standbyUnits: standby, activeUnits: Math.max(qty - standby, 0) };
};

// Every non-electrical connection, joined to its equipment and room, in one
// shape. Electrical rows have their own contract (equipmentLoads.js /
// electricalCircuitEngine.js) and are excluded here.
export function normalizeUtilityConnections(equipment = [], connections = [], roomsById = new Map()) {
  const eqById = new Map(equipment.map(e => [e.EquipmentID, e]));
  const out = [];
  for (const c of connections) {
    if (isElectricalType(c.UtilityType)) continue;
    const eq = eqById.get(c.EquipmentID);
    if (!eq) continue; // orphan row (equipment from another revision / deleted) — not this revision's demand
    const room = eq.RoomID != null ? roomsById.get?.(eq.RoomID) : null;
    out.push({
      connectionId: c.ConnectionID ?? null, equipmentId: eq.EquipmentID,
      tag: eq.Tag || null, equipmentDescription: eq.Description || null,
      roomId: eq.RoomID ?? null,
      roomLabel: eq.RoomID != null ? (room ? `${room.RoomNumber ? `${room.RoomNumber} — ` : ""}${room.RoomName}` : `room #${eq.RoomID}`) : (eq.LocationText || null),
      utilityType: c.UtilityType, kind: UTILITY_BY_KEY[c.UtilityType]?.kind || null,
      circuitDesc: c.CircuitDesc || null,
      flowValue: num(c.FlowValue), flowUnits: c.FlowUnits || null,
      pressureValue: num(c.PressureValue), pressureUnits: c.PressureUnits || null,
      branchSize: c.BranchSize || null,
      consumptionPerDay: num(c.ConsumptionPerDay), consumptionUnits: c.ConsumptionUnits || null,
      diversityOn: !(c.DiversityOn === false || c.DiversityOn === 0),
      ...unitsOf(eq),
      provenance: eq.LibraryItemID != null ? "library" : "project",
      notes: c.Notes || null,
    });
  }
  return out;
}

// ---- Phase C reference: legacy generic drops (reported, never totalled) ----------------------------

export const LEGACY_DROP_CRITERIA = { cda_drops: "cca", n2_drops: "nitrogen", vac_drops: "vacuum", pw_drops: "pw" };

// { utilityType: { count, byRoom: [{ roomId, roomLabel, count, source }] } }.
// A room's drop Room Elements (entity tagged SyncsToCriteriaField = the drop
// field, P0 Phase D migration — roomElements.js resolveLegacyCount) win;
// otherwise the room's OWN criterion row only (a project-level default is not
// a room drop — unchanged). `ctx` = { roomElementsByRoom, takeoffConditions,
// formulas }; without it every room reads its criterion row as before. Still
// reference only: no demand is attached to a drop here.
export function legacyDropsByUtility(rooms = [], criteria = {}, ctx = {}) {
  const out = {};
  for (const [fieldKey, utilityType] of Object.entries(LEGACY_DROP_CRITERIA)) {
    for (const room of rooms) {
      const fromElements = ctx.roomElementsByRoom ? resolveLegacyCount(fieldKey, room, criteria, ctx) : null;
      const source = fromElements?.source === "element" ? "element" : "legacy";
      const n = source === "element" ? fromElements.value : num(criteria[criterionKey("process", fieldKey, room.RoomID)]);
      if (n === null || n <= 0) continue;
      const entry = (out[utilityType] ||= { utilityType, fieldKey, count: 0, byRoom: [] });
      entry.count += n;
      entry.byRoom.push({ roomId: room.RoomID, roomLabel: `${room.RoomNumber ? `${room.RoomNumber} — ` : ""}${room.RoomName}`, count: n, source });
    }
  }
  return out;
}

// ---- Phase E: utility system design records (UtilitySystems, db/24) ---------------------------------
// Product decisions 2026-09-12. Kept as SEPARATE values per system:
//   ConnectedDemand  = Σ per-unit flow × ACTIVE units (Qty − StandbyUnits) —
//                      true standby is not normal operating demand unless the
//                      record's IncludeStandbyInConnected says the design
//                      basis lets it run concurrently (installedDemand = all
//                      units, kept for pipe/branch sizing context)
//   DiversityFactor  = resolved with provenance (below); default 1.00, never invented
//   DesignDemand     = ConnectedDemand × DiversityFactor
//   CapacityMargin   = resolved with provenance; default 0 %, never universal
//   RequiredCapacity = DesignDemand × (1 + CapacityMargin)
//   RedundancyStrategy → installed configuration; it NEVER reduces the
//                      required demand (386 SCFM required stays 386 with N+1:
//                      three 200-SCFM compressors, two duty, one standby)
//   InstalledCapacity = explicit, else UnitCount × UnitCapacityValue
// Diversity / coincidence is separate from equipment StandbyUnits.
export const DIVERSITY_SOURCES = [
  { key: "standard", label: "CleanSpace standard",          mark: "" },
  { key: "project",  label: "Project design basis",        mark: "‡" },
  { key: "scenario", label: "Equipment operating scenario", mark: "" },   // the connections' ON/OFF flags
  { key: "override", label: "Estimator override",          mark: "*" },
  { key: "default",  label: "Default 1.00 (no source)",    mark: "" },
];
export const MARGIN_SOURCES = [
  { key: "standard", label: "CleanSpace standard",   mark: "" },
  { key: "project",  label: "Project design basis", mark: "‡" },
  { key: "override", label: "Estimator override",   mark: "*" },
  { key: "default",  label: "Default 0 % (no source)", mark: "" },
];
export const REDUNDANCY_STRATEGIES = [
  { key: "none",     label: "None",   standbyFor: (duty) => 0 },
  { key: "n_plus_1", label: "N+1",    standbyFor: (duty) => 1 },
  { key: "n_plus_2", label: "N+2",    standbyFor: (duty) => 2 },
  { key: "2n",       label: "2N",     standbyFor: (duty) => duty },
  { key: "custom",   label: "Custom", standbyFor: () => null },
];
export const REDUNDANCY_BY_KEY = Object.fromEntries(REDUNDANCY_STRATEGIES.map(r => [r.key, r]));
const sourceLabel = (list, key) => list.find(x => x.key === key) || list[list.length - 1];
const variableKey = (prefix, utilityType) => `${prefix}_${String(utilityType || "").toUpperCase()}`;

// { value, source, label, mark }. The record's own source wins when it carries
// a usable value; 'scenario' = Σ ON × active ÷ connected from the connections;
// else the CleanSpace standard (per-utility key, then default) if present; else 1.
export function resolveDiversityFactor(record, { utilityType, tunables = {}, scenarioRatio = null } = {}) {
  const done = (value, source) => ({ value, source, label: sourceLabel(DIVERSITY_SOURCES, source).label, mark: sourceLabel(DIVERSITY_SOURCES, source).mark });
  const own = num(record?.DiversityFactor);
  const src = record?.DiversityFactorSource || null;
  if ((src === "override" || src === "project") && own !== null && own > 0 && own <= 1) return done(own, src);
  if (src === "scenario") return done(scenarioRatio === null ? 1 : Math.min(1, Math.max(0, scenarioRatio)), "scenario");
  const standard = num(tunables[variableKey("UTILITY_DIVERSITY", utilityType)]) ?? num(tunables.UTILITY_DIVERSITY_DEFAULT);
  if (standard !== null && standard > 0 && standard <= 1) return done(standard, "standard");
  return done(1, "default");
}
export function resolveCapacityMargin(record, { utilityType, tunables = {} } = {}) {
  const done = (value, source) => ({ value, source, label: sourceLabel(MARGIN_SOURCES, source).label, mark: sourceLabel(MARGIN_SOURCES, source).mark });
  const own = num(record?.CapacityMargin);
  const src = record?.CapacityMarginSource || null;
  if ((src === "override" || src === "project") && own !== null && own >= 0 && own <= 2) return done(own, src);
  const standard = num(tunables[variableKey("UTILITY_CAPACITY_MARGIN", utilityType)]) ?? num(tunables.UTILITY_CAPACITY_MARGIN_DEFAULT);
  if (standard !== null && standard >= 0 && standard <= 2) return done(standard, "standard");
  return done(0, "default");
}

// The installed configuration a redundancy strategy selects; all in the
// system's capacity units. { strategy, label, unitCount, unitCapacity,
// dutyUnits, standbyUnits, dutyCapacity, installedCapacity, configured, note }
export function redundancyOf(record) {
  const strategy = REDUNDANCY_BY_KEY[record?.RedundancyStrategy] ? record.RedundancyStrategy : "none";
  const def = REDUNDANCY_BY_KEY[strategy];
  const unitCount = num(record?.UnitCount), unitCapacity = num(record?.UnitCapacityValue);
  let dutyUnits = num(record?.DutyUnits);
  const expectedStandby = dutyUnits !== null ? def.standbyFor(dutyUnits) : null;
  if (dutyUnits === null && unitCount !== null && expectedStandby === null && strategy !== "custom") dutyUnits = null;
  const standbyUnits = unitCount !== null && dutyUnits !== null ? unitCount - dutyUnits : null;
  const explicit = num(record?.InstalledCapacityValue);
  const installedCapacity = explicit ?? (unitCount !== null && unitCapacity !== null ? unitCount * unitCapacity : null);
  const dutyCapacity = dutyUnits !== null && unitCapacity !== null ? dutyUnits * unitCapacity : (strategy === "none" ? installedCapacity : null);
  return {
    strategy, label: def.label, unitCount, unitCapacity, dutyUnits, standbyUnits, expectedStandby, dutyCapacity, installedCapacity,
    configured: installedCapacity !== null, note: record?.RedundancyNote || null,
  };
}

// Save payload for sp_SaveUtilitySystem from a draft record (DB column names).
export function utilitySystemPayload(draft, revisionId) {
  if (revisionId == null) throw new Error("utilitySystemPayload: revisionId is required");
  if (!draft?.UtilityType) throw new Error("utilitySystemPayload: UtilityType is required");
  return {
    utility_system_id: draft.UtilitySystemID ?? null, revision_id: revisionId, utility_type: draft.UtilityType,
    system_name: str(draft.SystemName),
    diversity_factor: num(draft.DiversityFactor), diversity_factor_source: draft.DiversityFactorSource || null,
    capacity_margin: num(draft.CapacityMargin), capacity_margin_source: draft.CapacityMarginSource || null,
    redundancy_strategy: REDUNDANCY_BY_KEY[draft.RedundancyStrategy] ? draft.RedundancyStrategy : "none", redundancy_note: str(draft.RedundancyNote),
    unit_count: num(draft.UnitCount), unit_capacity_value: num(draft.UnitCapacityValue), duty_units: num(draft.DutyUnits),
    installed_capacity_value: num(draft.InstalledCapacityValue), capacity_units: str(draft.CapacityUnits),
    include_standby_in_connected: draft.IncludeStandbyInConnected ? 1 : 0,
    source_equipment_id: num(draft.SourceEquipmentID), notes: draft.Notes ?? null, display_order: num(draft.DisplayOrder) ?? 100,
    // Phase G project sizing overrides (NULL = inherit the CleanSpace standard, db/93)
    material_key: str(draft.MaterialKey), sizing_method: str(draft.SizingMethod),
    min_velocity_fps: num(draft.MinVelocityFps), max_velocity_fps: num(draft.MaxVelocityFps), max_head_loss_ft_per_100ft: num(draft.MaxHeadLossFtPer100Ft),
  };
}
const str = (v) => { const t = v === null || v === undefined ? "" : String(v).trim(); return t || null; };

// ---- Phase D: system rollup ------------------------------------------------------------------------

// One utility system per utility type present in the connections.
//   { systems: [system], warnings: [] }
//   system = { utilityType, label, kind, governingUnits, governingPressureUnits,
//              connectionCount, equipmentCount, connectedDemand, diversifiedDemand,
//              designDemand, diversityRatio, onCount, offCount,
//              governingPressure: { value, units, connectionId } | null,
//              consumptionPerDay: { value, units } | null,
//              byRoom: [{ roomId, roomLabel, connected, diversified, connectionCount }],
//              connections: [normalized + { flowInGoverning, pressureInGoverning, included }],
//              excluded: [normalized rows whose units could not be converted],
//              legacyDrops: { count, byRoom } | null,
//              assumptions: [string], warnings: [{ code, message, connectionId? }] }
export function rollupUtilityDemand(equipment = [], connections = [], { rooms = [], criteria = {}, ctx = {}, systems = [], tunables = {} } = {}) {
  const roomsById = new Map(rooms.map(rm => [rm.RoomID, rm]));
  const normalized = normalizeUtilityConnections(equipment, connections, roomsById);
  const legacy = legacyDropsByUtility(rooms, criteria, ctx);
  const types = [...new Set([...normalized.map(n => n.utilityType), ...Object.keys(legacy)])];
  const order = Object.keys(UTILITY_BY_KEY);
  types.sort((a, b) => order.indexOf(a) - order.indexOf(b));

  const rolled = types.map(utilityType => {
    const def = UTILITY_BY_KEY[utilityType] || {};
    const record = systems.find(u => u.UtilityType === utilityType) || null;
    const governingUnits = def.flowUnits || normalized.find(n => n.utilityType === utilityType && n.flowUnits)?.flowUnits || null;
    const governingPressureUnits = def.pressureUnits || null;
    const rows = normalized.filter(n => n.utilityType === utilityType);
    const warnings = [];
    const conns = rows.map(n => {
      const flowInGoverning = n.flowValue === null ? null : convertFlow(n.flowValue, n.flowUnits, governingUnits);
      const pressureInGoverning = n.pressureValue === null || !governingPressureUnits ? null : convertPressure(n.pressureValue, n.pressureUnits, governingPressureUnits);
      let included = true;
      if (n.flowValue === null) {
        included = false;
        warnings.push({ code: "flow_missing", connectionId: n.connectionId, message: `${n.tag || n.equipmentDescription}: ${utilityLabel(utilityType)} connection has no flow value — excluded from the totals.` });
      } else if (flowInGoverning === null) {
        included = false;
        warnings.push({ code: "flow_units_incompatible", connectionId: n.connectionId, message: `${n.tag || n.equipmentDescription}: ${n.flowValue} ${n.flowUnits || "(no units)"} cannot be converted to ${governingUnits} — excluded from the totals, not silently converted.` });
      }
      if (n.pressureValue !== null && governingPressureUnits && pressureInGoverning === null) {
        warnings.push({ code: "pressure_units_incompatible", connectionId: n.connectionId, message: `${n.tag || n.equipmentDescription}: pressure ${n.pressureValue} ${n.pressureUnits || "(no units)"} cannot be converted to ${governingPressureUnits}.` });
      }
      return { ...n, flowInGoverning, pressureInGoverning, included };
    });
    const included = conns.filter(c => c.included);
    // Phase E: connected demand counts ACTIVE units only (standby is not normal
    // operating demand) unless the system record's design basis lets standby
    // run concurrently; installedDemand keeps every unit for sizing context.
    const includeStandby = !!record?.IncludeStandbyInConnected;
    const installedDemand = included.reduce((s, c) => s + c.flowInGoverning * c.qty, 0);
    const connectedDemand = included.reduce((s, c) => s + c.flowInGoverning * (includeStandby ? c.qty : c.activeUnits), 0);
    const diversifiedDemand = included.filter(c => c.diversityOn).reduce((s, c) => s + c.flowInGoverning * c.activeUnits, 0);
    const scenarioRatio = connectedDemand > 0 ? Math.min(1, diversifiedDemand / connectedDemand) : null;
    const diversity = resolveDiversityFactor(record, { utilityType, tunables, scenarioRatio });
    const designDemand = connectedDemand * diversity.value;
    const margin = resolveCapacityMargin(record, { utilityType, tunables });
    const requiredCapacity = designDemand * (1 + margin.value);
    const redundancy = redundancyOf(record);
    if (record) {
      if (redundancy.strategy !== "none" && !redundancy.configured) warnings.push({ code: "redundancy_unconfigured", message: `${utilityLabel(utilityType)}: ${redundancy.label} selected but no unit count / capacity entered — installed capacity unknown.` });
      if (redundancy.expectedStandby !== null && redundancy.standbyUnits !== null && redundancy.standbyUnits !== redundancy.expectedStandby) warnings.push({ code: "redundancy_inconsistent", message: `${utilityLabel(utilityType)}: ${redundancy.label} with ${redundancy.dutyUnits} duty implies ${redundancy.expectedStandby} standby, but ${redundancy.unitCount} installed leaves ${redundancy.standbyUnits}.` });
      if (redundancy.dutyCapacity !== null && requiredCapacity > 0 && redundancy.dutyCapacity < requiredCapacity) warnings.push({ code: "duty_capacity_below_required", message: `${utilityLabel(utilityType)}: duty capacity ${r(redundancy.dutyCapacity, 1)} ${governingUnits} is below the required ${r(requiredCapacity, 1)} ${governingUnits}.` });
      else if (redundancy.installedCapacity !== null && requiredCapacity > 0 && redundancy.installedCapacity < requiredCapacity) warnings.push({ code: "installed_capacity_below_required", message: `${utilityLabel(utilityType)}: installed capacity ${r(redundancy.installedCapacity, 1)} ${governingUnits} is below the required ${r(requiredCapacity, 1)} ${governingUnits}.` });
    }
    const onCount = conns.filter(c => c.diversityOn).length;
    for (const c of included) {
      if (c.activeUnits === 0 && c.diversityOn) warnings.push({ code: "no_active_units", connectionId: c.connectionId, message: `${c.tag || c.equipmentDescription}: Qty ${c.qty} with ${c.standbyUnits} standby leaves no operating unit — counted in connected demand only.` });
    }
    // Governing pressure: the highest required pressure among convertible rows.
    let governingPressure = null;
    for (const c of conns) {
      if (c.pressureInGoverning === null) continue;
      if (!governingPressure || c.pressureInGoverning > governingPressure.value) governingPressure = { value: c.pressureInGoverning, units: governingPressureUnits, connectionId: c.connectionId };
    }
    // Consumption per day: summed where the units are one family; otherwise reported as mixed.
    let consumptionPerDay = null;
    const withCons = conns.filter(c => c.consumptionPerDay !== null && c.consumptionUnits);
    if (withCons.length) {
      const target = withCons[0].consumptionUnits;
      let total = 0, mixed = false;
      for (const c of withCons) {
        const v = convertConsumption(c.consumptionPerDay, c.consumptionUnits, target);
        if (v === null) { mixed = true; break; }
        total += v * c.qty;
      }
      if (mixed) warnings.push({ code: "consumption_units_mixed", message: `${utilityLabel(utilityType)}: daily consumption is entered in incompatible unit families — not totalled.` });
      else consumptionPerDay = { value: total, units: target };
    }
    // By room (location-only equipment groups under its location text).
    const byRoomMap = new Map();
    for (const c of included) {
      const key = c.roomId != null ? `r${c.roomId}` : `l:${c.roomLabel || "unassigned"}`;
      const e = byRoomMap.get(key) || { roomId: c.roomId, roomLabel: c.roomLabel || "unassigned", connected: 0, diversified: 0, connectionCount: 0 };
      e.connected += c.flowInGoverning * c.qty;
      if (c.diversityOn) e.diversified += c.flowInGoverning * c.activeUnits;
      e.connectionCount += 1;
      byRoomMap.set(key, e);
    }
    const assumptions = [
      includeStandby
        ? "Connected demand = per-unit flow × ALL installed units — this system's design basis lets standby equipment run concurrently (record setting)."
        : "Connected demand = per-unit flow × active units (Qty − StandbyUnits): true standby is not normal operating demand. Installed demand (every unit) is kept for pipe/branch sizing context.",
      `Design demand = connected × diversity factor ${diversity.value} (${diversity.label}); required capacity = design demand × (1 + capacity margin ${Math.round(margin.value * 100)} % — ${margin.label}). Neither factor is invented: without a project / scenario / override value the CleanSpace standard applies (1.00 / 0 % until approved).`,
      redundancy.strategy === "none" ? "Redundancy: none selected — installed capacity is compared directly with the required capacity." : `Redundancy ${redundancy.label}: sets the installed configuration (${redundancy.unitCount ?? "?"} × ${redundancy.unitCapacity ?? "?"} ${governingUnits}, ${redundancy.dutyUnits ?? "?"} duty); it never reduces the required demand.`,
    ];
    if (legacy[utilityType]) assumptions.push(`${legacy[utilityType].count} legacy generic drop(s) (${legacy[utilityType].fieldKey}) are listed for reference only — no demand is attached and they are not in any total.`);
    return {
      utilityType, label: utilityLabel(utilityType), kind: def.kind || null,
      governingUnits, governingPressureUnits,
      connectionCount: conns.length, equipmentCount: new Set(conns.map(c => c.equipmentId)).size,
      record,
      installedDemand: r(installedDemand, 3), connectedDemand: r(connectedDemand, 3), diversifiedDemand: r(diversifiedDemand, 3),
      scenarioRatio: scenarioRatio === null ? null : r(scenarioRatio, 3),
      diversity: { ...diversity, value: r(diversity.value, 3) },
      designDemand: r(designDemand, 3),
      capacityMargin: margin,
      requiredCapacity: r(requiredCapacity, 3),
      redundancy,
      installedCapacity: redundancy.installedCapacity,
      capacityUnits: record?.CapacityUnits || governingUnits,
      diversityRatio: connectedDemand > 0 ? r(diversifiedDemand / connectedDemand, 3) : null,
      onCount, offCount: conns.length - onCount,
      governingPressure: governingPressure ? { ...governingPressure, value: r(governingPressure.value, 2) } : null,
      consumptionPerDay: consumptionPerDay ? { ...consumptionPerDay, value: r(consumptionPerDay.value, 2) } : null,
      byRoom: [...byRoomMap.values()].map(e => ({ ...e, connected: r(e.connected, 3), diversified: r(e.diversified, 3) })),
      connections: conns, excluded: conns.filter(c => !c.included),
      legacyDrops: legacy[utilityType] || null,
      assumptions, warnings,
    };
  });
  return { systems: rolled, warnings: rolled.flatMap(s => s.warnings.map(w => ({ ...w, utilityType: s.utilityType }))) };
}

// Plant / source requirement for one system — the end of the Phase E chain:
// required capacity (design demand × (1 + margin)) at the governing pressure,
// plus the redundancy strategy that shapes the installed configuration. It
// states what the source must supply; the equipment selection itself is the
// UtilitySystems record's own configuration (never derived here).
export function plantRequirement(system) {
  return {
    utilityType: system.utilityType, label: system.label,
    connectedDemand: system.connectedDemand, installedDemand: system.installedDemand,
    diversityFactor: system.diversity?.value ?? 1, diversitySource: system.diversity?.source ?? "default",
    designDemand: system.designDemand,
    capacityMargin: system.capacityMargin?.value ?? 0, capacityMarginSource: system.capacityMargin?.source ?? "default",
    requiredCapacity: system.requiredCapacity ?? system.designDemand,
    units: system.governingUnits,
    requiredPressure: system.governingPressure,
    redundancy: system.redundancy || redundancyOf(null),
    installedCapacity: system.installedCapacity ?? null,
    basis: "design demand = connected (active units) × diversity factor; required capacity = design demand × (1 + capacity margin); redundancy sets the installed configuration and never reduces the requirement",
  };
}
